(ns ui.editors.nev-view-editor.add-card-wizard
  (:require
   [ui.util :as uu]
   [re-frame.core :as rf]
   [ui.editors.nev-view-editor.sub :as sub]
   [ui.editors.nev-view-editor.event :as event]
   [ui.components.button.view :as button]
   [ui.editors.card.view :as card-editor]
   [ui.view.card.view :as card-view]
   [common.localization :as loc]
   [reagent.core :as r]
   [oops.core :as oops]
   [ui.editors.nev-view-editor.model :as model]
   [clojure.string :as string]))

(def wizard-step-options
  [{:value :card-type
    :label (loc/tr :view-editor/select-card-type)
    :step-number 1}
   {:value :card-template
    :label (loc/tr :view-editor/select-card-template)
    :step-number 2}
   {:value :configure-card
    :label (loc/tr :view-editor/configure-card)
    :step-number 3}])

(def step-numbers-by-step
  (->> wizard-step-options
       (map (fn [{:keys [value step-number]}]
              [value step-number]))
       (into {})))

(defn wizard-steps
  []
  (let [current-step @(rf/subscribe [::sub/add-card-wizard-step])
        current-step-number (get step-numbers-by-step current-step)]
    [:div
     {:class "relative inline-flex gap-10 items-center"}
     (->> wizard-step-options
          (map
           (fn [{:keys [value label step-number]}]
             (let [current? (= value current-step)
                   done? (< step-number current-step-number)]
               ^{:key (str value)}
               [:div
                {:class "relative z-10 flex flex-col gap-1 items-center"}
                [:div
                 {:class (uu/cm "border rounded-full h-6 w-6 flex items-center justify-center bg-base-light text-xs"
                                (when current?
                                  "bg-primary-regular text-primary-regular-text")
                                (when done?
                                  "bg-secondary-regular text-secondary-regular-text border-secondary-regular"))}
                 [:span
                  {:class "font-semibold"}
                  step-number]]
                [:p
                 {:class (uu/cm "text-sm m-0 font-bold"
                                (when-not current?
                                  "text-base-light-text-paler"))}
                 label]]))))
     [:div
      {:class "absolute top-3 left-14 right-14 h-px bg-base-regular"}]]))

(defn wizard-header-right
  []
  (let [current-step @(rf/subscribe [::sub/add-card-wizard-step])]
    [:div
     {:class "flex justify-end items-center gap-3"}
     (case current-step
       :configure-card [card-editor/save-card-button]
       nil)
      [button/square-secondary-hollow
      {:on-click #(rf/dispatch [::event/exit-add-card-wizard])}
      [:i.fal.fa-times]]
     ]))

(defn wizard-header-left
  []
  (let [current-step @(rf/subscribe [::sub/add-card-wizard-step])
        current-step-number (get step-numbers-by-step current-step)]
    [:div
     {:class "flex gap-5 items-center"}
    
     (when (< 1 current-step-number)
       [button/sm-secondary-hollow
        {:on-click #(rf/dispatch [::event/add-card-wizard-previous-step])}
        [:i.fal.fa-angle-left
         {:class "scale-125"}]
        (loc/tr :back)])]))

(defn wizard-header
  []
  [:div
   {:class "p-4 flex justify-between items-center"}
   [:div
    {:class "w-3/12"}
    [wizard-header-left]]
   [:div 
    {:class "flex justify-center w-9/12"}
    [wizard-steps]]
   [:div
    {:class "w-3/12 flex justify-end"}
    [wizard-header-right]
    ]])

(defn card-type-option
  [{:keys [value label description]}]
  [:div
   {:class "flex flex-col p-3 border border-solid border-base-light-border-paler rounded cursor-pointer bg-base-light hover:border-base-light-border-accent"
    :on-click #(rf/dispatch [::event/select-card-type value])}
   [:p
    {:class "text-lg text-semibold"}
    label]
   [:p
    {:class "text-sm m-0 text-base-light-text-paler"}
    description]])

(defn select-card-type-view
  []
  (let [card-type-options @(rf/subscribe [::sub/card-type-options])
        entity-type @(rf/subscribe [::sub/selected-top-level-section-entity-type])]
    [:div
     {:class "flex flex-col items-center gap-2"}
     [:div
      {:class "flex flex-col gap-5 lg:w-[500px] max-w-screen"}
      [:div
       {:class "flex flex-col items-center gap-3 text-center"}
       [:h4
        {:class "font-semibold text-2xl"}
        (loc/tr :view-editor/what-type-of-card-you-want-to-add)]
       [:p
        {:class "text-sm m-0 text-base-light-text-paler"}
        (loc/tr :view-editor/what-type-of-card-you-want-to-add-helper
                [(string/lower-case (loc/tr entity-type))])]]
      [:div
       {:class "flex flex-col gap-3"}
       (->> card-type-options
            (map (fn [opt]
                   ^{:key (str (:value opt))}
                   [:div
                    [card-type-option opt]])))]]]))

(defn card-preview-modal
  [{:keys [card set-ref close]}]
  (let []
    [:dialog
     {:ref set-ref
      :class "p-0 border-none rounded shadow-md"
      :on-click close}
     [:div
      {:on-click (fn [e]
                   (oops/ocall e "preventDefault")
                   (oops/ocall e "stopPropagation"))
       :class "w-[800px] max-w-screen relative"}
      [button/square-secondary-open
       {:class "absolute top-1 right-1"
        :on-click close}
       [:i.fal.fa-times]]
      [:div
       [card-view/view
        {:card card
         :entity-id "acme-inc"
         :card-state-path model/preview-card-state-full-path
         :preview-mode? true}]]]]))

(defn card-template-option
  [_]
  (let [modal-ref (atom nil)
        open-modal #(when-let [modal @modal-ref]
                      (oops/ocall modal "showModal"))
        close-modal #(when-let [modal @modal-ref]
                       (oops/ocall modal "close"))]
    (fn [{:keys [card]}]
      (let [{:keys [db/id card/title card/description]} card]
        [:<>
         [:div
          {:class "p-3 flex items-center justify-between 
                   border border-solid border-base-light-border-paler hover:border-base-light-border-accent
                   rounded cursor-pointer bg-base-light"
           :on-click (fn [_]
                       (rf/dispatch [::event/add-card-to-view id])
                       (rf/dispatch [::event/exit-add-card-wizard]))}
          [:div
           {:class "flex flex-col text-lg text-semibold"}
           [:p title]
           [:p
            {:class "m-0 text-sm text-base-light-text-paler"}
            description]]
          [:div
           [button/square-secondary-open
            {:on-click (fn [e]
                         (oops/ocall e "preventDefault")
                         (oops/ocall e "stopPropagation")
                         (open-modal))
             :tooltip (loc/tr :preview)}
            [:i.fal.fa-eye]]]]

         [card-preview-modal
          {:set-ref #(reset! modal-ref %)
           :close close-modal
           :card card}]]))))

(defn select-card-template-view
  []
  (let [card-type @(rf/subscribe [::sub/add-card-wizard-card-type])
        card-template-options @(rf/subscribe [::sub/card-template-options])]
    [:div
     {:class "flex flex-col items-center gap-2"}
     [:div
      {:class "flex flex-col gap-5  max-w-screen w-full lg:w-[500px]"}
      [:div
       {:class "flex justify-center items-center flex-col gap-3 text-center"}
       [:h4
        {:class "font-semibold text-2xl"}
        (loc/tr :view-editor/create-new-card-or-use-existing)]
       [:p
        {:class "text-sm m-0 text-complement-light-text-paler text-center"}
        (loc/tr :view-editor/create-new-card-or-use-existing-helper
                [(string/lower-case (loc/tr card-type))])]]
      [:div
       {:class "flex flex-col gap-5"}
       [button/sm-primary-solid
        {:on-click #(rf/dispatch [::event/open-card-configuration-step
                                  {:create-new-card? true}])}
        (loc/tr :view-editor/create-new-card)]
       (if (seq card-template-options)
         [:div
          {:class "flex flex-col gap-2"}
          [:p
           {:class "m-0 text-sm text-base-light-text font-semibold"}
           (loc/tr :view-editor/your-existing-cards)]
          [:div
           {:class "flex flex-col gap-2"}
           (->> card-template-options
                (map (fn [{:keys [card] :as item}]
                       ^{:key (str (:db/id card))}
                       [card-template-option item])))]]
         [:p
          {:class "text-sm m-0 text-base-light-text-paler "}
          (loc/tr :view-editor/you-dont-have-cards-of-this-type-yet)])]]]))

(defn configure-card-view
  []
  [:div
   {:class "flex flex-col h-full overflow-hidden"}
   [:div
    {:class "flex justify-between items-center bg-base-light py-5 border border-x-0 border-t-0"}
    [:div]
    [card-editor/tab-selector]
    [:div]]
   [:div
    {:class "grow overflow-hidden overflow-y-auto"}
    [card-editor/content]]])

(defn view
  []
  (let [current-step @(rf/subscribe [::sub/add-card-wizard-step])]
    [:div
     {:class "flex flex-col"}
     [wizard-header]
     [:div
      {:class "py-6 grow bg-base-light"}
      (case current-step
        :card-type [select-card-type-view]
        :card-template [select-card-template-view]
        :configure-card [configure-card-view]
        [:div "Unknown step"])]]))
